import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Loader2, MessageCircle, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

const AskTheWater = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'narrator',
      content: 'I am the water. I was here before the first body. I\'ll be here after the last. Ask me what you wish to know.'
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/narrator`, {
        message: userMessage,
        session_id: sessionId
      });

      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { role: 'narrator', content: response.data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'narrator', 
        content: 'The water stirs but does not speak. Try again.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-40 bg-[#C9A84C] text-[#080C10] p-4 rounded-full shadow-lg hover:bg-[#d4b55d] transition-colors"
        data-testid="ask-water-btn"
        aria-label="Ask The Water"
      >
        <MessageCircle size={24} />
      </motion.button>
      {/* Hidden toggle for nav trigger */}
      <button onClick={() => setIsOpen(!isOpen)} data-testid="ask-water-toggle" className="hidden" aria-hidden="true" />

      {/* Chat Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-6"
            onClick={() => setIsOpen(false)}
            data-testid="ask-water-overlay"
          >
            <motion.div
              initial={{ opacity: 0, y: 100, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 100, scale: 0.95 }}
              className="bg-[#0a0c10] border border-[#C9A84C]/20 sm:rounded-lg w-full sm:max-w-lg h-[80vh] sm:h-[600px] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              data-testid="ask-water-modal"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-[#C9A84C]/10">
                <div>
                  <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#C9A84C]">
                    ASK THE WATER
                  </h3>
                  <p className="font-['Outfit'] text-[#E8E0D0]/40 text-xs tracking-wider">
                    The omniscient narrator speaks
                  </p>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-[#E8E0D0]/50 hover:text-[#C9A84C] transition-colors"
                  data-testid="ask-water-close"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="ask-water-messages">
                {messages.map((message, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`chat-message p-4 rounded-lg ${message.role}`}
                  >
                    {message.role === 'narrator' && (
                      <span className="font-['Outfit'] text-[#C9A84C]/60 text-xs tracking-wider block mb-2">
                        THE WATER
                      </span>
                    )}
                    <p className={`font-['Outfit'] ${
                      message.role === 'narrator' 
                        ? 'text-[#E8E0D0]/90 italic' 
                        : 'text-[#E8E0D0]'
                    }`}>
                      {message.content}
                    </p>
                  </motion.div>
                ))}
                {isLoading && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="chat-message narrator p-4 rounded-lg"
                  >
                    <span className="font-['Outfit'] text-[#C9A84C]/60 text-xs tracking-wider block mb-2">
                      THE WATER
                    </span>
                    <Loader2 className="animate-spin text-[#C9A84C]" size={20} />
                  </motion.div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <form onSubmit={handleSubmit} className="p-4 border-t border-[#C9A84C]/10">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Ask the water..."
                    className="input-gold flex-1 py-3 px-4 rounded font-['Outfit'] text-sm"
                    disabled={isLoading}
                    data-testid="ask-water-input"
                  />
                  <button
                    type="submit"
                    disabled={!input.trim() || isLoading}
                    className="bg-[#C9A84C] text-[#080C10] p-3 rounded hover:bg-[#d4b55d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="ask-water-send"
                  >
                    <Send size={20} />
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AskTheWater;
